import { client as apolloClient } from '@xbcb/apollo-client';
import { RecordType } from '@xbcb/shared-types';
import { TERMINAL_WORK_ORDER_TASK_STATUS } from '@xbcb/work-order-types';
import { reportError } from '@xbcb/ui-utils';
import { message } from 'antd';
import {
  CreateWorkOrderTaskDocument,
  type CreateWorkOrderTaskMutation,
  GetMasterCiPlReworkContextDocument,
  type GetMasterCiPlReworkContextQuery,
  PlacementType,
  ShipmentCommercialInvoiceDetailStatus,
  WorkOrderStatus,
  WorkOrderTaskStatus,
  WorkOrderTaskType,
} from '__generated__/graphql';
import { executeMutation } from './executeMutation';
import { getRecordPath } from './getRecordPath';
import {
  updateWorkOrderStatus,
  updateWorkOrderTaskStatus,
} from './sharedQueries';
import { UPDATE_SHCID } from './handleRejectTask.query';

const BLOCKED_REASON_TAG = {
  key: 'BLOCKED_REASON',
  value: 'SELLER_DOCUMENTS_REJECTED',
};
const TERMINAL_WORK_ORDER_TASK_STATUSES = new Set<string>(
  TERMINAL_WORK_ORDER_TASK_STATUS,
);

interface ReworkMasterCiPlParams {
  workOrderId: string;
  currentUserId: string;
  navigate: (path: string) => void;
}

type ReworkEntry = GetMasterCiPlReworkContextQuery['usConsumptionEntry'];
type ReworkStateMachineState = ReworkEntry['stateMachine']['states'][number];
type ReworkWorkOrderTask = ReworkEntry['tasks'][number];
type ReworkTaskIdentity = Pick<ReworkWorkOrderTask, 'id' | 'version'>;
type ReworkTask = ReworkTaskIdentity &
  Partial<Pick<ReworkWorkOrderTask, 'status' | 'tags'>>;
type ReworkShipment = NonNullable<ReworkEntry['group']['shipment']>;
type ReworkShipmentLeg = NonNullable<ReworkShipment['legs'][number]>;
type ReworkShcid = NonNullable<ReworkShipmentLeg['commercialInvoiceDetail']>;

const isEntryCompletionState = (state?: ReworkStateMachineState): boolean =>
  state?.taskDefinition?.workOrderTaskType ===
  WorkOrderTaskType.US_CONSUMPTION_ENTRY_CONSOLIDATION_COMPLETION;

const isCibTask = (task: ReworkWorkOrderTask): boolean =>
  task.definition?.workOrderTaskType ===
  WorkOrderTaskType.US_CONSUMPTION_ENTRY_BUILD_INVOICE;

const getReworkContext = (entry: ReworkEntry) => {
  const states = entry.stateMachine.states;
  const currentState = states.find((state) => state.name === entry.state);
  const cibState = states.find(
    (state) =>
      state.taskDefinition?.workOrderTaskType ===
        WorkOrderTaskType.US_CONSUMPTION_ENTRY_BUILD_INVOICE &&
      isEntryCompletionState(
        states.find((nextState) => nextState.name === state.next),
      ),
  );
  const openTasks = entry.tasks.filter(
    (task) =>
      !task.deleted && !TERMINAL_WORK_ORDER_TASK_STATUSES.has(task.status),
  );

  return {
    shipment: entry.group.shipment,
    shcid: entry.group.shipment?.legs[0]?.commercialInvoiceDetail,
    currentState,
    cibState,
    openTasks,
    cibTask: openTasks.find(isCibTask),
  };
};

export const isHydraMasterCiPlReadyForRework = (
  entry?: ReworkEntry,
): boolean => {
  if (!entry?.operator?.id) return false;

  const { shipment, shcid, currentState, cibState, openTasks } =
    getReworkContext(entry);
  const isWaitingForSplitReview =
    entry.status === WorkOrderStatus.IN_PROGRESS &&
    isEntryCompletionState(currentState) &&
    openTasks.length === 0;
  const isRecoveringRework =
    entry.status === WorkOrderStatus.BLOCKED &&
    currentState?.name === cibState?.name &&
    openTasks.length <= 1 &&
    openTasks.every(isCibTask);

  return (
    shipment?.placementType === PlacementType.AOI_ORIGINAL &&
    shcid?.status === ShipmentCommercialInvoiceDetailStatus.SUBMITTED &&
    Boolean(cibState) &&
    (isWaitingForSplitReview || isRecoveringRework)
  );
};

const setWorkOrderToBlocked = async (
  entry: ReworkEntry,
  cibState: ReworkStateMachineState,
): Promise<void> =>
  executeMutation({
    mutation: updateWorkOrderStatus,
    successMessage: '',
    variables: {
      id: entry.id,
      version: entry.version,
      input: {
        status: WorkOrderStatus.BLOCKED,
        state: cibState.name,
      },
    },
    shouldThrowError: true,
  });

const createReworkCibTask = async (
  entry: ReworkEntry,
  cibState: ReworkStateMachineState,
  currentUserId: string,
): Promise<ReworkTaskIdentity> => {
  let createdTask: ReworkTaskIdentity | undefined;
  const previousCibTaskCount = entry.tasks.filter(isCibTask).length;

  await executeMutation({
    mutation: CreateWorkOrderTaskDocument,
    successMessage: '',
    variables: {
      idempotencyKey: `${entry.id}#${cibState.name}#master-cipl-rework:${previousCibTaskCount}`,
      input: {
        workOrder: { id: entry.id },
        operator: { id: entry.operator.id },
        assignee: { id: currentUserId },
        definition: {
          id: cibState.taskDefinition!.id,
          version: cibState.taskDefinition!.version,
        },
        minimumSkillRequired:
          cibState.taskDefinition!.minimumSkillRequired || undefined,
      },
    },
    shouldThrowError: true,
    onSuccess: async (data) => {
      createdTask = (data as CreateWorkOrderTaskMutation).createWorkOrderTask
        .record;
    },
  });

  if (!createdTask) throw new Error('Rework CIB task was not created');
  return createdTask;
};

const setCibTaskToBlocked = async (task: ReworkTaskIdentity): Promise<void> =>
  executeMutation({
    mutation: updateWorkOrderTaskStatus,
    successMessage: '',
    variables: {
      id: task.id,
      version: task.version,
      input: {
        status: WorkOrderTaskStatus.BLOCKED,
        tags: [BLOCKED_REASON_TAG],
      },
    },
    shouldThrowError: true,
  });

const rejectShipmentCommercialInvoiceDetail = async (
  shcid: ReworkShcid,
): Promise<void> =>
  executeMutation({
    mutation: UPDATE_SHCID,
    variables: {
      id: shcid.id,
      version: shcid.version,
      input: {
        status: ShipmentCommercialInvoiceDetailStatus.REJECTED,
      },
    },
    successMessage: 'Master CI/PL rework started',
    shouldThrowError: true,
  });

const isCibTaskBlockedForSellerDocuments = (task: ReworkTask): boolean =>
  task.status === WorkOrderTaskStatus.BLOCKED &&
  Boolean(
    task.tags?.some(
      ({ key, value }) =>
        key === BLOCKED_REASON_TAG.key && value === BLOCKED_REASON_TAG.value,
    ),
  );

export const handleReworkMasterCiPl = async ({
  workOrderId,
  currentUserId,
  navigate,
}: ReworkMasterCiPlParams): Promise<void> => {
  try {
    const { data } = await apolloClient.query({
      query: GetMasterCiPlReworkContextDocument,
      variables: { id: workOrderId },
      fetchPolicy: 'network-only',
    });
    const entry = data.usConsumptionEntry;

    if (!isHydraMasterCiPlReadyForRework(entry)) {
      throw new Error('Master CI/PL is not ready for rework');
    }

    const {
      shcid,
      cibState,
      cibTask: existingCibTask,
    } = getReworkContext(entry);

    if (entry.status !== WorkOrderStatus.BLOCKED) {
      await setWorkOrderToBlocked(entry, cibState!);
    }

    const cibTask =
      existingCibTask ||
      (await createReworkCibTask(entry, cibState!, currentUserId));

    if (!isCibTaskBlockedForSellerDocuments(cibTask)) {
      await setCibTaskToBlocked(cibTask);
    }

    // Reject last because the next SHCID transition unblocks this CIB task.
    await rejectShipmentCommercialInvoiceDetail(shcid!);

    navigate(
      `${getRecordPath({
        recordType: RecordType.US_CONSUMPTION_ENTRY,
        id: entry.id,
      })}?task=${WorkOrderTaskType.US_CONSUMPTION_ENTRY_BUILD_INVOICE}&taskId=${
        cibTask.id
      }`,
    );
  } catch (error) {
    const reportedError =
      error instanceof Error ? error : new Error(String(error));
    reportError(reportedError);
    void message.error(
      `Failed to rework Master CI/PL: ${
        reportedError.message || 'Unknown error'
      }`,
      5.0,
    );
  }
};
